import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { useTetherToast } from "@tether-web-portal/components/toast/useTetherToast";
import { api } from "../api";
import { getApiErrorMessage } from "../api-types";
import { useAuth } from "../../auth/useAuth";
import {
  OrganisationBillingConfig,
  PropertyBillingConfig,
  UpdateOrganisationBillingConfigInput,
  UpdatePropertyBillingConfigInput,
} from "@tether-web-portal/types/BillingConfig";

export enum BillingConfigQueryKeys {
  ORG_BILLING_CONFIG = "org-billing-config",
  PROPERTY_BILLING_CONFIG = "property-billing-config",
  ORG_LOGO_URL = "org-billing-logo-url",
  PROPERTY_LOGO_URL = "property-billing-logo-url",
}

interface LogoUploadTarget {
  uploadUrl: string;
  s3Key: string;
}

interface LogoUrlResponse {
  url: string | null;
}

// Presigned view URLs expire (15 min server-side); refetch comfortably inside
// that window so a cached URL never 403s on the user.
const LOGO_URL_STALE_MS = 10 * 60 * 1000;

/**
 * Upload a logo file straight to S3 via a presigned PUT. This bypasses the `api`
 * client deliberately — the presigned URL is pre-authorised, and adding our
 * Authorization/JSON headers would break the signature. The Content-Type MUST
 * match the type sent when minting the URL (the backend pins it into the
 * signature).
 */
async function uploadLogoToPresignedUrl(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!res.ok) {
    throw new Error(`Logo upload failed (${res.status})`);
  }
}

export const useGetOrganisationBillingConfig = (
  options?: UseQueryOptions<OrganisationBillingConfig | null, Error>,
) => {
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  // The endpoint resolves the org from the JWT, so the org id never reaches the
  // URL — but it MUST be in the cache key. Otherwise, under staleTime: Infinity,
  // org A's cached billing/tax/branding renders for org B after an in-SPA switch.
  return useQuery<OrganisationBillingConfig | null, Error>(
    [BillingConfigQueryKeys.ORG_BILLING_CONFIG, organisationId],
    () => api.get("v2/organisationbillingconfig"),
    { enabled: Boolean(organisationId), staleTime: Infinity, ...options },
  );
};

export const useUpdateOrganisationBillingConfigMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  const { errorToast, successToast } = useTetherToast();
  return useMutation(
    ({ input }: { input: UpdateOrganisationBillingConfigInput }) =>
      api.put<OrganisationBillingConfig>("v2/organisationbillingconfig", input),
    {
      onError: (e: Error) =>
        errorToast({ title: "Unable to update tenant billing settings", description: getApiErrorMessage(e) }),
      onSuccess: () => {
        successToast({ title: "Tenant billing settings updated" });
        queryClient.invalidateQueries([BillingConfigQueryKeys.ORG_BILLING_CONFIG, organisationId]);
        onSuccess?.();
      },
    },
  );
};

export const useGetPropertyBillingConfig = (
  propertyId: string,
  options?: UseQueryOptions<PropertyBillingConfig | null, Error>,
) =>
  useQuery<PropertyBillingConfig | null, Error>(
    [BillingConfigQueryKeys.PROPERTY_BILLING_CONFIG, propertyId],
    () => api.get("v2/propertybillingconfig", { propertyId }),
    { enabled: Boolean(propertyId), staleTime: Infinity, ...options },
  );

export const useUpdatePropertyBillingConfigMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();
  return useMutation(
    ({ input }: { input: UpdatePropertyBillingConfigInput }) =>
      api.put<PropertyBillingConfig>("v2/propertybillingconfig", input, { propertyId }),
    {
      onError: (e: Error) =>
        errorToast({ title: "Unable to update property overrides", description: getApiErrorMessage(e) }),
      onSuccess: () => {
        successToast({ title: "Property billing overrides updated" });
        queryClient.invalidateQueries([BillingConfigQueryKeys.PROPERTY_BILLING_CONFIG, propertyId]);
        onSuccess?.();
      },
    },
  );
};

export const useDeletePropertyBillingConfigMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();
  return useMutation(() => api.del("v2/propertybillingconfig", undefined, { propertyId }), {
    onError: (e: Error) =>
      errorToast({ title: "Unable to clear property overrides", description: getApiErrorMessage(e) }),
    onSuccess: () => {
      successToast({ title: "Property overrides cleared" });
      queryClient.invalidateQueries([BillingConfigQueryKeys.PROPERTY_BILLING_CONFIG, propertyId]);
      onSuccess?.();
    },
  });
};

// ---------------------------------------------------------------------------
// Invoice logo upload (org default + property override)
//
// Flow: POST /logo-upload-url → PUT the file straight to the presigned S3 URL →
// PUT /logo to confirm + persist the key. The view URL is a separate short-lived
// presigned GET (logo-url) so the bytes never travel through this API.
// ---------------------------------------------------------------------------

export const useGetOrganisationLogoUrl = () => {
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  return useQuery<string | null, Error>(
    [BillingConfigQueryKeys.ORG_LOGO_URL, organisationId],
    async () => (await api.get<LogoUrlResponse>("v2/organisationbillingconfig/logo-url")).url,
    { enabled: Boolean(organisationId), staleTime: LOGO_URL_STALE_MS },
  );
};

export const useUploadOrganisationLogoMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  const { errorToast, successToast } = useTetherToast();
  return useMutation(
    async (file: File) => {
      const target = await api.post<LogoUploadTarget>("v2/organisationbillingconfig/logo-upload-url", {
        fileType: file.type,
        fileSize: file.size,
      });
      await uploadLogoToPresignedUrl(target.uploadUrl, file);
      return api.put<OrganisationBillingConfig>("v2/organisationbillingconfig/logo", { s3Key: target.s3Key });
    },
    {
      onError: (e: Error) => errorToast({ title: "Unable to upload logo", description: getApiErrorMessage(e) }),
      onSuccess: () => {
        successToast({ title: "Logo updated" });
        queryClient.invalidateQueries([BillingConfigQueryKeys.ORG_BILLING_CONFIG, organisationId]);
        queryClient.invalidateQueries([BillingConfigQueryKeys.ORG_LOGO_URL, organisationId]);
        onSuccess?.();
      },
    },
  );
};

export const useDeleteOrganisationLogoMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  const { errorToast, successToast } = useTetherToast();
  return useMutation(() => api.del<OrganisationBillingConfig>("v2/organisationbillingconfig/logo"), {
    onError: (e: Error) => errorToast({ title: "Unable to remove logo", description: getApiErrorMessage(e) }),
    onSuccess: () => {
      successToast({ title: "Logo removed" });
      queryClient.invalidateQueries([BillingConfigQueryKeys.ORG_BILLING_CONFIG, organisationId]);
      queryClient.invalidateQueries([BillingConfigQueryKeys.ORG_LOGO_URL, organisationId]);
      onSuccess?.();
    },
  });
};

export const useGetPropertyLogoUrl = (propertyId: string) =>
  useQuery<string | null, Error>(
    [BillingConfigQueryKeys.PROPERTY_LOGO_URL, propertyId],
    async () => (await api.get<LogoUrlResponse>("v2/propertybillingconfig/logo-url", { propertyId })).url,
    { enabled: Boolean(propertyId), staleTime: LOGO_URL_STALE_MS },
  );

export const useUploadPropertyLogoMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();
  return useMutation(
    async (file: File) => {
      // `post` doesn't take a query arg, so propertyId rides in the URL.
      const target = await api.post<LogoUploadTarget>(
        `v2/propertybillingconfig/logo-upload-url?propertyId=${encodeURIComponent(propertyId)}`,
        { fileType: file.type, fileSize: file.size },
      );
      await uploadLogoToPresignedUrl(target.uploadUrl, file);
      return api.put<PropertyBillingConfig | null>(
        "v2/propertybillingconfig/logo",
        { s3Key: target.s3Key },
        { propertyId },
      );
    },
    {
      onError: (e: Error) => errorToast({ title: "Unable to upload logo", description: getApiErrorMessage(e) }),
      onSuccess: () => {
        successToast({ title: "Logo updated" });
        queryClient.invalidateQueries([BillingConfigQueryKeys.PROPERTY_BILLING_CONFIG, propertyId]);
        queryClient.invalidateQueries([BillingConfigQueryKeys.PROPERTY_LOGO_URL, propertyId]);
        onSuccess?.();
      },
    },
  );
};

export const useDeletePropertyLogoMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();
  return useMutation(
    () => api.del<PropertyBillingConfig | null>("v2/propertybillingconfig/logo", undefined, { propertyId }),
    {
      onError: (e: Error) => errorToast({ title: "Unable to remove logo", description: getApiErrorMessage(e) }),
      onSuccess: () => {
        successToast({ title: "Logo removed" });
        queryClient.invalidateQueries([BillingConfigQueryKeys.PROPERTY_BILLING_CONFIG, propertyId]);
        queryClient.invalidateQueries([BillingConfigQueryKeys.PROPERTY_LOGO_URL, propertyId]);
        onSuccess?.();
      },
    },
  );
};
